import { z } from "zod/v4-mini";

import { Schema, cz } from "@/utils/zodUtils.ts";

export type BasePumpEvent = {
  id: string;
  date: string;
};

export type PumpCartridgeFilledEvent = BasePumpEvent & {
  category: "cartridge_filled";
  insulinDisplay?: number;
  insulinActual?: number;
};

export type PumpTubingFilledEvent = BasePumpEvent & {
  category: "tubing_filled";
  primeSize?: number;
};

export type PumpCannulaFilledEvent = BasePumpEvent & {
  category: "cannula_filled";
  primeSize?: number;
};

export type PumpProfileChangeEvent = BasePumpEvent & {
  category: "profile_changed";
};

export type PumpPodChangeEvent = BasePumpEvent & {
  category: "pod_changed";
};

export type PumpPodActivatedEvent = BasePumpEvent & {
  category: "pod_activated";
};

export type PumpPodDeactivatedEvent = BasePumpEvent & {
  category: "pod_deactivated";
};

export type PumpEvent =
  | PumpCartridgeFilledEvent
  | PumpTubingFilledEvent
  | PumpCannulaFilledEvent
  | PumpProfileChangeEvent
  | PumpPodChangeEvent
  | PumpPodActivatedEvent
  | PumpPodDeactivatedEvent;

export type ClosedLoopUserMode = "normal" | "exercising" | "sleeping";

export type ClosedLoopUserModeChangeEvent = BasePumpEvent & {
  category: "closed_loop_user_mode_changed";
  data: {
    mode: ClosedLoopUserMode;
    previousMode?: ClosedLoopUserMode;
  };
};

export type ClosedLoopPumpMode = "no_control" | "open_loop" | "closed_loop" | "pining";

export type ClosedLoopPumpModeChangeEvent = BasePumpEvent & {
  category: "closed_loop_pump_mode_changed";
  data: {
    mode: ClosedLoopPumpMode;
    previousMode?: ClosedLoopPumpMode;
  };
};

export type ClosedLoopEvents = {
  userMode: ClosedLoopUserModeChangeEvent[];
  pumpMode: ClosedLoopPumpModeChangeEvent[];
};

export const pumpEventSchema: Schema<PumpEvent> = cz.camelCase(
  z.intersection(
    z.object({
      id: cz.id(),
      date: cz.datetime(),
    }),
    z.discriminatedUnion("category", [
      cz.camelCaseObject({
        category: z.literal("cartridge_filled"),
        insulin_display: z.optional(z.number()),
        insulin_actual: z.optional(z.number()),
      }),
      cz.camelCaseObject({
        category: z.literal(["tubing_filled", "cannula_filled"]),
        prime_size: z.optional(z.number()),
      }),
      z.object({
        category: z.literal(["profile_changed", "pod_changed", "pod_activated", "pod_deactivated"]),
      }),
    ]),
  ),
);

export const closedLoopEventSchema: Schema<ClosedLoopEvents> = cz.transform(
  cz.camelCaseObject({
    user_mode_events: cz.forcedArray(
      cz.camelCaseObject({
        id: cz.id(),
        date: cz.datetime(),
        category: z.literal("closed_loop_user_mode_changed"),
        data: cz.camelCaseObject({
          mode: z.literal(["normal", "exercising", "sleeping"]),
          previous_mode: z.optional(z.literal(["normal", "exercising", "sleeping"])),
        }),
      }),
    ),
    pump_mode_events: cz.forcedArray(
      cz.camelCaseObject({
        id: cz.id(),
        date: cz.datetime(),
        category: z.literal("closed_loop_pump_mode_changed"),
        data: cz.camelCaseObject({
          mode: z.literal(["no_control", "open_loop", "closed_loop", "pining"]),
          previous_mode: z.optional(z.literal(["no_control", "open_loop", "closed_loop", "pining"])),
        }),
      }),
    ),
  }),
  data => ({
    pumpMode: data.pumpModeEvents,
    userMode: data.userModeEvents,
  }),
);

export const getPumpEventTranslation = (category: PumpEvent["category"]): string => {
  switch (category) {
    case "cannula_filled":
      return "models.pumpEventCategory.cannulaFilled";
    case "cartridge_filled":
      return "models.pumpEventCategory.cartridgeFilled";
    case "tubing_filled":
      return "models.pumpEventCategory.tubingFilled";
    case "profile_changed":
      return "models.pumpEventCategory.profileChanged";
    case "pod_changed":
      return "models.pumpEventCategory.podChanged";
    case "pod_activated":
      return "models.pumpEventCategory.podActivated";
    case "pod_deactivated":
      return "models.pumpEventCategory.podDeactivated";
  }
};

export const getPumpModeTranslation = (mode: ClosedLoopPumpMode | "manual") => {
  switch (mode) {
    case "no_control":
      return "models.pumpMode.noControl";
    case "open_loop":
      return "models.pumpMode.openLoop";
    case "closed_loop":
      return "models.pumpMode.closedLoop";
    case "pining":
      return "models.pumpMode.pining";
    case "manual": // Aggregation of all cases that are not closed loop
      return "models.pumpMode.manual";
  }
};

export const getUserModeTranslation = (mode: ClosedLoopUserMode) => {
  switch (mode) {
    case "exercising":
      return "models.userMode.exercising";
    case "sleeping":
      return "models.userMode.sleeping";
    case "normal":
      return "models.userMode.normal";
  }
};
